'use client';

import { useState, useEffect, useCallback } from 'react';
import { vehiclesApi, bookingsApi, chatApi, reviewsApi } from './api';
import {
  Vehicle,
  Booking,
  ChatConversation,
  ChatMessage,
  Review,
  VehicleFilters,
  BookingFilters,
  ChatFilters,
  ReviewFilters,
  CreateBookingRequest,
  StartConversationRequest,
  SendMessageRequest,
  BookingStatus,
} from './types';

// ============================================
// GENERIC FETCH HOOK
// ============================================

interface UseApiState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// VEHICLES HOOK
// ============================================

export function useVehicles(filters?: VehicleFilters) {
  const [state, setState] = useState<UseApiState<Vehicle[]>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const fetchVehicles = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await vehiclesApi.getAll(filters);
      setState({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: (err as Error).message });
    }
  }, [filters?.category]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  return { ...state, refetch: fetchVehicles };
}

export function useVehicle(id: number | null) {
  const [state, setState] = useState<UseApiState<Vehicle>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const fetchVehicle = useCallback(async () => {
    if (!id) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await vehiclesApi.getById(id);
      setState({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: (err as Error).message });
    }
  }, [id]);

  useEffect(() => {
    fetchVehicle();
  }, [fetchVehicle]);

  return { ...state, refetch: fetchVehicle };
}

// ============================================
// BOOKINGS HOOK
// ============================================

export function useBookings(filters?: BookingFilters) {
  const [state, setState] = useState<UseApiState<Booking[]>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const fetchBookings = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await bookingsApi.getAll(filters);
      setState({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: (err as Error).message });
    }
  }, [filters?.status, filters?.vehicleId]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  return { ...state, refetch: fetchBookings };
}

export function useBooking(id: number | null) {
  const [state, setState] = useState<UseApiState<Booking>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const fetchBooking = useCallback(async () => {
    if (!id) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await bookingsApi.getById(id);
      setState({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: (err as Error).message });
    }
  }, [id]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  return { ...state, refetch: fetchBooking };
}

export function useCreateBooking() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createBooking = async (data: CreateBookingRequest): Promise<Booking | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await bookingsApi.create(data);
      return response.data;
    } catch (err) {
      setError((err as Error).message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { createBooking, isLoading, error };
}

export function useUpdateBookingStatus() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateStatus = async (id: number, status: BookingStatus): Promise<Booking | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await bookingsApi.update(id, { status });
      return response.data;
    } catch (err) {
      setError((err as Error).message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { updateStatus, isLoading, error };
}

// ============================================
// CHAT HOOKS
// ============================================

export function useConversations(filters?: ChatFilters) {
  const [state, setState] = useState<UseApiState<ChatConversation[]>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const fetchConversations = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await chatApi.getConversations(filters);
      setState({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: (err as Error).message });
    }
  }, [filters?.status]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  return { ...state, refetch: fetchConversations };
}

export function useConversation(id: number | null) {
  const [state, setState] = useState<UseApiState<ChatConversation>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const fetchConversation = useCallback(async () => {
    if (!id) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await chatApi.getConversation(id);
      setState({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: (err as Error).message });
    }
  }, [id]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  return { ...state, refetch: fetchConversation };
}

export function useChat() {
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load conversation ID from localStorage
  useEffect(() => {
    const storedId = localStorage.getItem('chatConversationId');
    if (storedId) {
      setConversationId(parseInt(storedId));
    }
  }, []);

  // Fetch messages when conversation ID changes
  useEffect(() => {
    if (conversationId) {
      fetchMessages();
    }
  }, [conversationId]);

  const fetchMessages = async () => {
    if (!conversationId) return;
    try {
      const response = await chatApi.getConversation(conversationId);
      setMessages(response.data.messages || []);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const startConversation = async (data: StartConversationRequest): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await chatApi.startConversation(data);
      const newId = response.data.id;
      setConversationId(newId);
      localStorage.setItem('chatConversationId', String(newId));
      setMessages(response.data.messages || []);
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (data: SendMessageRequest): Promise<boolean> => {
    if (!conversationId) return false;
    setIsLoading(true);
    setError(null);
    try {
      const response = await chatApi.sendMessage(conversationId, data);
      setMessages((prev) => [...prev, response.data]);
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearConversation = () => {
    setConversationId(null);
    setMessages([]);
    localStorage.removeItem('chatConversationId');
  };

  return {
    conversationId,
    messages,
    isLoading,
    error,
    startConversation,
    sendMessage,
    fetchMessages,
    clearConversation,
    hasConversation: !!conversationId,
  };
}

// ============================================
// REVIEWS HOOK
// ============================================

export function useReviews(filters?: ReviewFilters) {
  const [state, setState] = useState<UseApiState<Review[]>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const fetchReviews = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await reviewsApi.getAll(filters);
      setState({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: (err as Error).message });
    }
  }, [filters?.featured]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  return { ...state, refetch: fetchReviews };
}

// ============================================
// ADMIN DASHBOARD STATS HOOK
// ============================================

interface DashboardStats {
  totalVehicles: number;
  activeBookings: number;
  pendingBookings: number;
  openChats: number;
  recentBookings: Booking[];
  recentConversations: ChatConversation[];
}

export function useDashboardStats() {
  const [state, setState] = useState<UseApiState<DashboardStats>>({
    data: null,
    isLoading: true,
    error: null,
  });

  const fetchStats = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const [vehiclesRes, bookingsRes, conversationsRes] = await Promise.all([
        vehiclesApi.getAll(),
        bookingsApi.getAll(),
        chatApi.getConversations(),
      ]);

      const bookings = bookingsRes.data;
      const conversations = conversationsRes.data;

      const stats: DashboardStats = {
        totalVehicles: vehiclesRes.data.length,
        activeBookings: bookings.filter((b) => b.status === 'confirmed').length,
        pendingBookings: bookings.filter((b) => b.status === 'pending').length,
        openChats: conversations.filter((c) => c.status === 'open').length,
        recentBookings: bookings.slice(0, 5),
        recentConversations: conversations.slice(0, 5),
      };

      setState({ data: stats, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: (err as Error).message });
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { ...state, refetch: fetchStats };
}
